import { isValidTimeZone, timeZoneOffsetMinutes } from '@gr/core/time/zoned-time';

// WS-RULE-2: выбор часового пояса блога из списка IANA — подписи со сдвигом и поиск по ним.

export interface TimeZoneOption {
  /** Имя из базы IANA — то, что сохраняется в блоге. */
  name: string;
  /** Сдвиг от UTC в минутах на момент построения списка (летнее время учтено). */
  offsetMinutes: number;
  /** «Europe/Moscow · UTC+3». */
  label: string;
}

/** «UTC+3», «UTC+5:30», «UTC-2:30», «UTC+0». */
export function formatUtcOffset(minutes: number): string {
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  const rest = abs % 60;
  return `UTC${sign}${Math.floor(abs / 60)}${rest ? `:${String(rest).padStart(2, '0')}` : ''}`;
}

/** Подпись пояса; неизвестное движку имя (старый псевдоним) показывается как есть. */
export function timeZoneOption(name: string, now: Date): TimeZoneOption {
  if (!isValidTimeZone(name)) return { name, offsetMinutes: 0, label: name };
  const offsetMinutes = timeZoneOffsetMinutes(now, name);
  return { name, offsetMinutes, label: `${name} · ${formatUtcOffset(offsetMinutes)}` };
}

/** Все пояса со сдвигами, с запада на восток, внутри сдвига — по имени. */
export function timeZoneOptions(names: readonly string[], now: Date): TimeZoneOption[] {
  return names.map((name) => timeZoneOption(name, now)).sort((a, b) => a.offsetMinutes - b.offsetMinutes || a.name.localeCompare(b.name));
}

/**
 * Слово запроса как сдвиг: «+3», «-5», «3», «UTC+5:30», «GMT-03:00», «+0530». Без знака — плюс.
 * Не сдвиг (буквы, больше 14 часов) — null.
 */
export function parseUtcOffset(token: string): number | null {
  const match = /^(?:utc|gmt)?([+\-−]?)(\d{1,2})(?::?(\d{2}))?$/i.exec(token.trim());
  if (!match) return null;
  const hours = Number(match[2]);
  const minutes = Number(match[3] ?? 0);
  if (hours > 14 || minutes >= 60) return null;
  const total = hours * 60 + minutes;
  return match[1] === '-' || match[1] === '−' ? -total : total;
}

const normalize = (text: string): string => text.toLocaleLowerCase().replaceAll('_', ' ');

/**
 * Поиск: каждое слово запроса — часть подписи (без учёта регистра, «_» как пробел) или точный
 * сдвиг; совпасть должны все слова. «mosc», «new york», «+5:30», «europe +1».
 */
export function searchTimeZones(options: readonly TimeZoneOption[], query: string): TimeZoneOption[] {
  const words = normalize(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return [...options];
  const tests = words.map((word) => {
    const offset = parseUtcOffset(word);
    return offset === null ? (option: TimeZoneOption) => normalize(option.label).includes(word) : (option: TimeZoneOption) => option.offsetMinutes === offset;
  });
  return options.filter((option) => tests.every((test) => test(option)));
}

/** Страница найденного — для автокомплита. */
export function timeZonePage(options: readonly TimeZoneOption[], query: string, offset: number, size: number): { items: TimeZoneOption[]; hasMore: boolean } {
  const found = searchTimeZones(options, query);
  return { items: found.slice(offset, offset + size), hasMore: offset + size < found.length };
}
